import { useCallback } from 'react';
import { toast, type ToastContent } from 'react-toastify';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import Toast from '@/components/ui/Toast';
import * as Sentry from '@sentry/react';

export type HandleErrorProps<T> = {
  message?: string | string[] | Record<keyof T, string[]>;
  warning?: boolean;
};

export default function useHandleError<T extends FieldValues>(setError?: UseFormSetError<T>) {
  const handleError = useCallback(
    (error: HandleErrorProps<T>) => {
      const { message, warning } = error;

      if (typeof message === 'string' && warning == true) {
        toast.warning(Toast as ToastContent, {
          toastId: 'signin-warn',
          position: 'top-center',
          className: 'border border-off-white',
          data: {
            title: 'Action Required',
            message,
          },
        });
      } else if (typeof message === 'string') {
        toast.error(Toast as ToastContent, {
          toastId: 'msgstr-error-toast',
          position: 'top-center',
          className: 'border border-off-white',
          data: {
            title: 'Error Occurred',
            message,
          },
        });
      } else if (Array.isArray(message)) {
        toast.error(Toast as ToastContent, {
          toastId: 'msgarr-error-toast',
          position: 'top-center',
          className: 'border border-off-white',
          data: {
            title: 'Error Occurred',
            message: message[0],
          },
        });
      } else if (message && typeof message === 'object' && setError) {
        Object.entries(message).forEach(([field, messages]) => {
          setError(field as Path<T>, {
            type: 'server',
            message: Array.isArray(messages) ? messages[0] : String(messages),
          });
        });
      } else {
        toast.error(Toast as ToastContent, {
          toastId: 'common-error-toast',
          position: 'top-center',
          className: 'border border-off-white',
          data: {
            title: 'Error Occurred',
            message: 'Something went wrong. Please try again later.',
          },
        });
      }

      Sentry.captureException(new Error('Error occurred in useHandleError Function'), {
        tags: {
          errorType: 'useHandleError',
        },
        contexts: {
          errorContext: Object.fromEntries(
            Object.entries({
              contextName: 'useHandleError',
              error,
              message,
              warning,
            }).filter(([, value]) => value !== undefined),
          ),
        },
      });
    },
    [setError],
  );

  return handleError;
}
